import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { typeList } from "../../models/Template";
import {
    DeleteTemplate,
    GetTemplate,
    ResetDeleteTemplateError,
} from "../../store/actions/TemplateActions";
import styles from "./TemplateIndex.module.css";
import TemplateCreateModal from "./TemplateCreateModal";
import { Button, CircularProgress, Modal, Snackbar } from "@material-ui/core";
import { useInitFlag } from "../../../components/function/useInitFlag";
import { useInitText } from "../../../components/function/useInitText";
import TimelineTopMenuArea from "../timelineTop/TimelineTopMenuArea";
import { MoreVert } from "@material-ui/icons";
import PuiTypography from "../../../components/elements/PuiTypography";
import { useInitNumber } from "../../../components/function/useInitNumber";
import RenameObjectModal from "../../../common/components/modal/RenameObjectModal";
import { GetTemplateUrlFromId } from "../../../utils/TimelineUtils";
import PuiMessage from "../../../components/widgets/PuiMessage";
import Alert from "@material-ui/lab/Alert/Alert";

function TemplateIndex() {
    const dispatch = useDispatch();

    // Storeからデータ取得
    const {
        user,
        templateList,
        isLoading,
        isDeleteTemplateFailed,
        isDeleteTemplateCompleted,
        deleteError,
    } = useSelector((state: AppState) => ({
        user: state.user.user,
        templateList: state.template.templateList,
        isLoading: state.template.isLoading,
        isDeleteTemplateFailed: state.template.isDeleteTemplateFailed,
        isDeleteTemplateCompleted: state.template.isDeleteTemplateCompleted,
        deleteError: state.template.deleteError,
    }));

    useEffect(() => {
        //エラーメッセージリセット
        ResetDeleteTemplateError();
        // テンプレート一覧APIコール
        dispatch(GetTemplate(typeList));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [visiblePreviewModal, ShowPreviewModal] = useInitFlag(false);
    const [previewModalUrl, SetPreviewModalUrl] = useInitText();
    const [previewModalStatus, setPreviewModalStatus] = useInitText();
    const [isDeleting, setIsDeleting] = useInitFlag();

    // 画像クリックでプレビューモーダル表示
    const handleClickImage = (id: number, status: string) => {
        SetPreviewModalUrl(GetTemplateUrlFromId(id));
        setPreviewModalStatus(status);
        ShowPreviewModal(true);
    };

    const handleClosePreviewModal = () => {
        ShowPreviewModal(false);
        SetPreviewModalUrl("");
        setPreviewModalStatus("");
    };

    // テンプレート削除確認ダイアログ
    const [isOpenVerifyDeleteModal, setIsOpenVerifyDeleteModal] = useState(false);
    const [templateDelId, setTemplateDelId] = useState(0);

    // テンプレート名変更処理
    const [editTemplateId, setEditTemplateId] = useInitNumber();
    const [editTemplateName, setEditTemplateName] = useInitText();

    // フォルダ名編集ポップアップ表示フラグ
    const [isOpenRenameTemplateModal, setOpenRenameTemplateModal] = useInitFlag();

    // エラーメッセージ表示フラグ
    const [showDeleteError, setShowDeleteError] = useInitFlag();
    // 完了メッセージ表示フラグ
    const [showDeleteSuccess, setShowDeleteSuccess] = useInitFlag();

    // 削除アイコンを押下の場合、
    const handleDelTemplate = (id: number) => {
        setTemplateDelId(id);
        setIsOpenVerifyDeleteModal(true);
        handleMenu(-1);
    };

    // エラーメッセージ閉じる処理
    const CloseDeleteError = () => {
        setIsDeleting(false);
        setShowDeleteError(false);
        ResetDeleteTemplateError();
    };

    // 完了メッセージ閉じる処理
    const CloseDeleteSuccess = () => {
        setIsDeleting(false);
        setShowDeleteSuccess(false);
        ResetDeleteTemplateError();
    };
    
    // 名前変更ボタンを押下の場合、
    const handleRenameTemplate = (id: number, name: string) => {
        setEditTemplateId(id);
        setEditTemplateName(name);
        setOpenRenameTemplateModal(true);
        handleMenu(-1);
    };

    // 削除確認ダイアログを閉じる
    const CloseVerifyDeleteModal = () => {
        setIsOpenVerifyDeleteModal(false);
        setTemplateDelId(0);
    };

    // 削除ダイアログのキャンセルボタンを押下の場合、
    const handleCancelBtn = () => {
        CloseVerifyDeleteModal();
    };

    // 削除ダイアログのOkボタンを押下の場合、
    const handleOKBtn = () => {
        // テンプレート削除APIコール
        if (user) dispatch(DeleteTemplate(templateDelId, Number(user.id), typeList));
        setIsDeleting(true);
        // モーダルを閉じる
        CloseVerifyDeleteModal();
    };
    const [templateType, setTemplateType] = useState<string>("OP");

    const handleTemplateType = (type: string) => {
        setTemplateType(type);
    };

    const [openTemplateMenu, setOpenTemplateMenu] = useState<number>(-1);

    const handleMenu = (index: number) => {
        if (openTemplateMenu === index) {
            setOpenTemplateMenu(-1);
        } else {
            setOpenTemplateMenu(index);
        }
    };

    // アップロードステータス状況取得
    useEffect(() => {
        let IS_UPLOADING = false;
        let timeoutId: NodeJS.Timeout;
        if (templateList && templateList.length > 0) {
            IS_UPLOADING = templateList.some((template) => template.status === "UPLOADING");

            if (IS_UPLOADING) {
                timeoutId = setTimeout(() => dispatch(GetTemplate(typeList)), 5000);
            }
        }
        // クリーンアップ
        return () => {
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line
    }, [templateList]);

    // エラーメッセージ表示
    useEffect(() => {
        if (isDeleteTemplateFailed && isDeleting) {
            setShowDeleteError(true);
            setIsDeleting(false);
        } else if (isDeleteTemplateCompleted && isDeleting) {
            setShowDeleteSuccess(true);
            setIsDeleting(false);
        }
        // eslint-disable-next-line
    }, [isDeleteTemplateFailed, isDeleteTemplateCompleted]);

    return (
        <div className={styles.root}>
            <div className={styles.row}>
                <TimelineTopMenuArea menuType="テンプレート" />
                <div className={styles.mainDiv}>
                    <div className={styles.buttonArea}>
                        <div style={{ borderBottom: "2px solid #606060" }}>
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={() => handleTemplateType("OP")}
                                className={styles.button}
                                style={{
                                    color:
                                        templateType === "OP"
                                            ? "var(--white)"
                                            : "rgba(255,255,255,0.5)",
                                    backgroundColor:
                                        templateType === "OP" ? "rgba(255,255,255,0.1)" : "#202327",
                                }}
                            >
                                オープニング
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                type="submit"
                                onClick={() => handleTemplateType("ED")}
                                className={styles.button}
                                style={{
                                    color:
                                        templateType === "ED"
                                            ? "var(--white)"
                                            : "rgba(255,255,255,0.5)",
                                    backgroundColor:
                                        templateType === "ED" ? "rgba(255,255,255,0.1)" : "#202327",
                                }}
                            >
                                エンディング
                            </Button>
                        </div>
                        <TemplateCreateModal />
                    </div>
                    <div className={styles.temlateArea}>
                        {templateList &&
                            templateList.length > 0 &&
                            templateList.map(
                                (template, index) =>
                                    template.type === templateType && (
                                        <div key={template.id} className={styles.template}>
                                            <img
                                                src={template.imageUrl}
                                                alt={template.name}
                                                className={styles.imageUrl}
                                                onClick={() =>
                                                    handleClickImage(template.id, template.status)
                                                }
                                                onError={(e) =>
                                                    (e.currentTarget.src = require("../../../assets/image/NoImage.jpg"))
                                                }
                                            />
                                            <div
                                                onClick={() => handleMenu(index)}
                                                className={styles.icon}
                                            >
                                                <MoreVert />
                                            </div>
                                            {index === openTemplateMenu && (
                                                <div className={styles.menuItem}>
                                                    <div
                                                        onClick={() =>
                                                            handleRenameTemplate(
                                                                template.id,
                                                                template.name
                                                            )
                                                        }
                                                    >
                                                        <PuiTypography>名前を変更</PuiTypography>
                                                    </div>
                                                    <div
                                                        onClick={() =>
                                                            handleDelTemplate(template.id)
                                                        }
                                                    >
                                                        <PuiTypography> 削除</PuiTypography>
                                                    </div>
                                                </div>
                                            )}
                                            {openTemplateMenu !== -1 && (
                                                <div
                                                    className={styles.bg}
                                                    onClick={() => handleMenu(-1)}
                                                />
                                            )}

                                            <div className={styles.templateName}>
                                                <PuiTypography> {template.name}</PuiTypography>
                                            </div>
                                        </div>
                                    )
                            )}
                    </div>
                    {openTemplateMenu !== -1 && (
                        <div className={styles.modalBg} onClick={() => setOpenTemplateMenu(-1)} />
                    )}
                </div>

                {/* 動画プレビュー再生モーダル */}
                <Modal
                    open={visiblePreviewModal}
                    onClose={handleClosePreviewModal}
                    className={styles.previewModal}
                >
                    <>
                        {(previewModalStatus?.toLowerCase() === "uploading" ||
                            previewModalStatus?.toLowerCase() === "error") && (
                            <div className={styles.errorDiv}>
                                {previewModalStatus?.toLowerCase() === "error"
                                    ? "エラーが発生しました。テンプレートを再度アップロードしてください。"
                                    : "このテンプレートは処理中です。しばらくしてから再度ご確認ください"}
                            </div>
                        )}

                        <video
                            autoPlay
                            src={previewModalUrl}
                            controls
                            controlsList="nofullscreen nodownload"
                            disablePictureInPicture
                        ></video>
                    </>
                </Modal>

                {/* 削除確認モーダル表示 */}
                <Modal
                    open={isOpenVerifyDeleteModal}
                    onClose={CloseVerifyDeleteModal}
                    className={styles.deleteModal}
                >
                    <div className={styles.deleteTemplateDiv}>
                        <div style={{ color: "var(--white)" }}>
                            対象のテンプレートを削除しますか？
                        </div>
                        <div className={styles.deleteBtnDiv}>
                            <Button
                                className={styles.deleteBtn}
                                onClick={handleCancelBtn}
                                style={{
                                    color: "var(--white)",
                                    backgroundColor: "#606060",
                                }}
                            >
                                キャンセル
                            </Button>
                            <Button
                                className={styles.deleteBtn}
                                onClick={handleOKBtn}
                                style={{
                                    color: "var(--white)",
                                    backgroundColor: "var(--studio-btn-bg)",
                                }}
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* テンプレート名変更モーダルを呼び出す */}
                <RenameObjectModal
                    isOpenModal={isOpenRenameTemplateModal}
                    setOpenModal={setOpenRenameTemplateModal}
                    id={editTemplateId}
                    editName={editTemplateName}
                    setEditName={setEditTemplateName}
                    type={"テンプレート"}
                />
                <Modal open={isLoading}>
                    <div className={styles.noContent}>
                        <CircularProgress
                            size={60}
                            style={{ color: "var(--loading-bg)", marginBottom: "15px" }}
                        />
                        <div className={styles.text}>
                            <PuiTypography>{"アップロード中です・・・"}</PuiTypography>
                        </div>
                    </div>
                </Modal>
            </div>
            <PuiMessage
                open={showDeleteError}
                type="error"
                title={deleteError}
                duration={3000}
                onClose={CloseDeleteError}
            />
            <Snackbar autoHideDuration={5000} open={showDeleteSuccess} onClose={CloseDeleteSuccess}>
                <Alert severity="success">
                    <div>テンプレートの削除を完了しました。</div>
                </Alert>
            </Snackbar>
        </div>
    );
}

export default TemplateIndex;
